import type { PlacedBrick } from '@bw/core';
import type { ProjectDocument, Wall } from '@bw/document';
import { buildRoof, ridgeDirectionFor } from './roof.js';
import type { RoofModel } from './roof.js';
import { joineryParts } from './joinery.js';
import { footprintOf } from './footprint.js';
import { findOverlaps, resolveJunctions } from './junctions.js';
import { openingsForWall } from './openings.js';
import { brickToWorld, wallToPlan } from './transform.js';
import type {
  BuildingModel,
  BuildingOpening,
  PlacedWall,
  PlanExtent,
  Vec2,
  WorldBrick,
} from './types.js';

/**
 * How far a wall reaches along its own x axis.
 *
 * Taken from what the wall actually contains: the bricks if there are any,
 * otherwise the envelope a bond was generated into. A wall with neither has no
 * length, and that is reported as `null` rather than invented — the plan view
 * draws it as a marker, which is truthful about there being nothing there yet.
 */
export function planExtentOf(wall: Wall): PlanExtent | null {
  // The design line wins when there is one. A wall set out from its corners
  // has courses running past the junction to fill the outside of it, and
  // reading the outline off the brickwork would let those courses drag the
  // footprint out with them until it stopped closing.
  if (wall.span !== null) return { minX: wall.span.minX, maxX: wall.span.maxX };
  const bounds = wall.model.bounds();
  if (bounds !== null) return { minX: bounds.minX, maxX: bounds.maxX };
  const envelope = wall.envelope;
  if (envelope !== null && envelope.w > 0) {
    return { minX: envelope.x, maxX: envelope.x + envelope.w };
  }
  return null;
}

/** One wall, positioned in the world. */
export function placeWall(wall: Wall): PlacedWall {
  const extent = planExtentOf(wall);
  const thickness = wall.placement.thicknessMm;
  const corners: Vec2[] =
    extent === null
      ? []
      : [
          wallToPlan(wall.placement, extent.minX, 0),
          wallToPlan(wall.placement, extent.maxX, 0),
          wallToPlan(wall.placement, extent.maxX, thickness),
          wallToPlan(wall.placement, extent.minX, thickness),
        ];
  return {
    id: wall.id,
    name: wall.name,
    placement: wall.placement,
    extent,
    planCorners: corners,
    centreline:
      extent === null
        ? null
        : [
            wallToPlan(wall.placement, extent.minX, thickness / 2),
            wallToPlan(wall.placement, extent.maxX, thickness / 2),
          ],
    brickCount: wall.model.size,
    source: wall,
  };
}

/**
 * The whole building, from the document.
 *
 * Everything geometric that the 3D view will need is decided here, in code
 * with no DOM and no renderer, so it can be tested by comparing numbers rather
 * than by looking at pictures. That is the point of this package.
 */
export function assembleBuilding(project: ProjectDocument): BuildingModel {
  const placed = resolveJunctions(project.walls.map(placeWall));
  const openings: BuildingOpening[] = [];
  let brickCount = 0;
  for (const wall of placed) {
    openings.push(...openingsForWall(wall.source));
    brickCount += wall.brickCount;
  }

  let min: Vec2 | null = null;
  let max: Vec2 | null = null;
  for (const wall of placed) {
    for (const corner of wall.planCorners) {
      min =
        min === null
          ? corner
          : { x: Math.min(min.x, corner.x), z: Math.min(min.z, corner.z) };
      max =
        max === null
          ? corner
          : { x: Math.max(max.x, corner.x), z: Math.max(max.z, corner.z) };
    }
  }

  const footprint = footprintOf(placed);
  // Which way the ridge runs decides which walls are eaves and which are
  // gable ends, and the eaves height depends on that — so it is settled before
  // the roof is built rather than inside it.
  const ridge = ridgeDirectionFor(footprint, project.roof);
  const eavesY = eavesHeight(placed, ridge);
  const roof = buildRoof(footprint, project.roof, eavesY);
  return {
    walls: placed,
    openings,
    footprint,
    overlaps: findOverlaps(placed),
    brickCount,
    bounds: min === null || max === null ? null : { min, max },
    roof: maskBuiltGables(roof, placed, ridge, project.roof.overhangMm),
    joinery: joineryParts(placed, openings, project.joinery),
  };
}

/**
 * Where the roof starts: the top of the walls it slopes down to.
 *
 * Taken from the bricks rather than from a setting, because the wall's height
 * is however many courses were actually laid — there is no other truth to it.
 *
 * `ridge` is the direction a gable roof's ridge runs, and it matters. The
 * tallest wall is the right answer for a hip, where the roof comes down to
 * every wall equally. It is the WRONG answer the moment a gable end is built
 * in masonry: a gable is a wall deliberately carried up past the eaves to meet
 * the slope, so measuring the eaves from it puts the roof at the point of the
 * gable and builds a second roof on top of the first. Walls running across the
 * ridge are those gable ends, and they are left out.
 *
 * With no ridge — a hip, or no roof at all — every wall counts, exactly as
 * before.
 */
export function eavesHeight(walls: readonly PlacedWall[], ridge: Vec2 | null = null): number {
  let top = 0;
  let counted = 0;
  for (const wall of walls) {
    const bounds = wall.source.model.bounds();
    if (bounds === null) continue;
    if (ridge !== null && !runsAlong(wall, ridge)) continue;
    top = Math.max(top, wall.placement.origin.y + bounds.maxY);
    counted += 1;
  }
  // A gable roof whose ends are the only walls there are has no eaves wall to
  // measure. Falling back to all of them is better than returning zero and
  // dropping the roof to the ground.
  return counted === 0 && ridge !== null ? eavesHeight(walls, null) : top;
}

/** Does this wall run along the ridge — an eaves wall rather than a gable end? */
function runsAlong(wall: PlacedWall, ridge: Vec2): boolean {
  const line = wall.centreline;
  if (line === null) return true;
  const dx = line[1].x - line[0].x;
  const dz = line[1].z - line[0].z;
  const length = Math.hypot(dx, dz);
  if (length === 0) return true;
  return Math.abs((dx / length) * ridge.x + (dz / length) * ridge.z) > Math.SQRT1_2;
}

/**
 * Drop the gable panels that masonry has already built.
 *
 * A gable roof leaves a vertical triangle at each end, and `buildRoof` fills
 * it — which is right when the end is open, and wrong when the wall below was
 * carried up as a gable in brick. The generated panel sits out at the verge,
 * in front of the brickwork, so it does not merely duplicate the gable: it
 * hides it, patterning and all.
 *
 * So a panel is kept only where nothing was built to fill it.
 */
export function maskBuiltGables(
  roof: RoofModel,
  walls: readonly PlacedWall[],
  ridge: Vec2 | null,
  overhangMm: number,
): RoofModel {
  if (ridge === null || roof.gables.length === 0) return roof;
  const along = (x: number, z: number): number => x * ridge.x + z * ridge.z;
  // A panel stands at the verge, a whole overhang out from the wall that would
  // fill it, so the reach has to allow for that and for the wall's thickness.
  const ends = walls.filter((wall) => {
    if (wall.centreline === null || runsAlong(wall, ridge)) return false;
    const bounds = wall.source.model.bounds();
    return bounds !== null && wall.placement.origin.y + bounds.maxY > roof.eavesY + 1;
  });
  if (ends.length === 0) return roof;

  const gables = roof.gables.filter((panel) => {
    if (panel.length === 0) return true;
    let at = 0;
    for (const corner of panel) at += along(corner.x, corner.z);
    at /= panel.length;
    return !ends.some((wall) => {
      const line = wall.centreline as readonly [Vec2, Vec2];
      const middle = along((line[0].x + line[1].x) / 2, (line[0].z + line[1].z) / 2);
      return Math.abs(middle - at) <= overhangMm + wall.placement.thicknessMm;
    });
  });
  return gables.length === roof.gables.length ? roof : { ...roof, gables };
}

/**
 * Every brick in the building, in world space.
 *
 * A generator because at the scale this is designed for — fifty thousand
 * bricks — materialising the whole array is 3.2 MB of transforms that the
 * caller usually wants to stream straight into an instance buffer.
 */
export function* worldBricks(building: BuildingModel): Generator<WorldBrick> {
  for (const wall of building.walls) {
    for (const brick of wall.source.model.bricks as readonly PlacedBrick[]) {
      yield brickToWorld(wall.id, wall.placement, brick);
    }
  }
}
